import {
  colorsData,
  CustomerAddress,
  CustomerAddressType,
  CustomerDeliveryAddressFormValues,
  CustomerDeliveryAddressPayload,
  CustomerInvoiceAddress,
  CustomerInvoiceAddressFormValues,
  CustomerInvoiceAddressPayload,
  InvoiceType
} from './types'

export class AddressService {
  emptyAddInvoiceAddressFormValues: CustomerInvoiceAddressFormValues = {
    id: '',
    iconColor: colorsData[0].value,
    iconType: 'company',
    name: '',
    invoiceType: InvoiceType.company,
    companyName: '',
    taxId: '',
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    city: '',
    houseNumber: '',
    flatNumber: '',
    isDefault: false
  }

  /**
   * See: https://lokalnyrolnik-api.test.desmart.live/docs/#account-POSTapi-addresses
   * @param values Form values from {@link FormAddInvoiceAddress}.
   * @param type {CustomerAddressType} can be either `delivery` or `invoice`.
   * @returns Promise
   */
  mapInvoiceFormValuesToPayload(
    values: CustomerInvoiceAddressFormValues,
    type: CustomerAddressType
  ): CustomerInvoiceAddressPayload {
    const payload: CustomerInvoiceAddressPayload = {
      type,
      name: values.name,
      invoiceType: values.invoiceType,
      companyName: values.companyName,
      nip: values.taxId,
      firstName: values.firstName,
      lastName: values.lastName,
      city: values.city,
      postalCode: values.postalCode,
      street: values.street,
      houseNumber: values.houseNumber,
      iconColor: values.iconColor,
      iconType: values.iconType,
      id: values.id,
      isDefault: values.isDefault
    }

    if (values.flatNumber) {
      payload.flatNumber = values.flatNumber
    } else if (!values.flatNumber) {
      payload.flatNumber = ''
    }

    return payload
  }

  mapInvoiceResponseToFormValues(
    response?: CustomerInvoiceAddress
  ): CustomerInvoiceAddressFormValues {
    if (!response) {
      return this.emptyAddInvoiceAddressFormValues
    }

    return {
      iconColor: response?.iconColor,
      iconType: response?.iconType,
      id: response.id,
      name: response.name,
      invoiceType: response.invoiceType,
      companyName: response.companyName,
      taxId: response.nip,
      firstName: response.firstName,
      lastName: response.lastName,
      street: response.street,
      postalCode: response.postalCode,
      city: response.city,
      houseNumber: response.houseNumber,
      flatNumber: response?.flatNumber || '',
      isDefault: response.isDefault
    }
  }

  emptyAddDeliveryAddressFormValues: CustomerDeliveryAddressFormValues = {
    id: '',
    iconColor: colorsData[0].value,
    iconType: 'home',
    name: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    postalCode: '',
    city: '',
    street: '',
    houseNumber: '',
    staircaseNumber: '',
    flatNumber: '',
    floor: '',
    clientDeliveryNotes: '',
    isDefault: false,
    latitude: 0,
    longitude: 0
  }

  mapDeliveryFormValuesToPayload(
    values: CustomerDeliveryAddressFormValues,
    type: CustomerAddressType
  ): CustomerDeliveryAddressPayload {
    const payload: CustomerDeliveryAddressPayload = {
      type,
      id: values.id,
      iconColor: values.iconColor,
      iconType: values.iconType,
      name: values.name,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      postalCode: values.postalCode,
      city: values.city,
      street: values.street,
      houseNumber: values.houseNumber,
      staircaseNumber: values.staircaseNumber,
      flatNumber: values.flatNumber,
      floor: values.floor,
      clientDeliveryNotes: values.clientDeliveryNotes,
      isDefault: values.isDefault,
      latitude: values.latitude,
      longitude: values.longitude
    }

    return payload
  }

  mapDeliveryResponseToFormValues(
    response?: CustomerAddress
  ): CustomerDeliveryAddressFormValues {
    if (!response) {
      return this.emptyAddDeliveryAddressFormValues
    }

    return {
      iconColor: response?.iconColor,
      iconType: response?.iconType,
      id: response.id,
      name: response.name,
      firstName: response.firstName,
      lastName: response.lastName,
      phoneNumber: response.phoneNumber,
      postalCode: response.postalCode,
      city: response.city,
      street: response.street,
      latitude: response.latitude,
      longitude: response.longitude,
      houseNumber: response.houseNumber,
      staircaseNumber: response.staircaseNumber,
      flatNumber: response.flatNumber,
      floor: response.floor,
      clientDeliveryNotes: response.clientDeliveryNotes,
      isDefault: response.isDefault
    }
  }
}

export const isLatitudeAndLongitudeError = (
  latitude?: number | null,
  longitude?: number | null
) => {
  const numberLatitude = Number(latitude)
  const numberLongitude = Number(longitude)

  if (
    !numberLatitude ||
    !numberLongitude ||
    numberLatitude === 0 ||
    numberLongitude === 0
  ) {
    return true
  }

  return false
}

export const AddressServiceSingleton = new AddressService()
